// Company settings: the details printed on every invoice, the tax rate and
// currency applied to new invoices, the logo, the default UI language, and the
// mail server everything is sent through.
//
// The mail server is a second form rather than another card in the first one:
// it holds a credential and has its own test, and correcting a postcode should
// not save a relay password as a side effect.

import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { fieldErrors, useAsync, useErrorMessage } from '../lib/hooks';
import { useT } from '../lib/i18n';
import { useSession } from '../lib/session';
import { useToast } from '../components/Toast';
import { Icon } from '../components/Icon';
import {
  Checkbox, ErrorAlert, LoadingRow, SelectField, TextField,
} from '../components/ui';
import {
  LOCALES,
  type Currency, type Locale, type MailSecurity, type MailSettings, type MailSettingsInput,
  type Settings as SettingsShape,
} from '@shared/types';
import type { TranslationKey } from '@shared/i18n/index';

const LOCALE_LABELS: Record<Locale, string> = { es: 'Español', en: 'English' };

export function Settings() {
  const t = useT();
  const toast = useToast();
  const { refresh } = useSession();
  const toMessage = useErrorMessage();

  const { data, loading, error, reload } = useAsync(
    async () => {
      const [settings, currencies, mail] = await Promise.all([
        api.settings(), api.currencies(), api.mailSettings(),
      ]);
      return { settings, currencies, mail };
    }, []);

  if (loading && !data) return <LoadingRow />;
  if (error) return <ErrorAlert message={error} onRetry={reload} />;
  if (!data) return null;

  return (
    <>
      <div className="page-head">
        <div><h1 className="page-head__title">{t('settings.title')}</h1></div>
      </div>
      <SettingsForm
        initial={data.settings}
        currencies={data.currencies}
        onSaved={() => { toast.success(t('settings.saved')); void refresh(); reload(); }}
        onError={(err) => toast.error(toMessage(err))}
      />
      <MailServerForm
        initial={data.mail}
        onSaved={() => { toast.success(t('settings.saved')); reload(); }}
      />
    </>
  );
}

function SettingsForm({ initial, currencies, onSaved, onError }: {
  initial: SettingsShape;
  currencies: Currency[];
  onSaved: () => void;
  onError: (err: unknown) => void;
}) {
  const t = useT();
  const toast = useToast();
  const toMessage = useErrorMessage();
  const fileInput = useRef<HTMLInputElement>(null);

  const [form, setForm] = useState(initial);
  const [logoPath, setLogoPath] = useState(initial.logoPath);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string>();
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => { setForm(initial); setLogoPath(initial.logoPath); }, [initial]);

  const set = <K extends keyof SettingsShape>(key: K, value: SettingsShape[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    setErrors({});
    setFormError(undefined);
    try {
      await api.updateSettings({
        companyName: form.companyName, taxId: form.taxId, address: form.address,
        city: form.city, region: form.region, postalCode: form.postalCode,
        phone: form.phone, email: form.email, taxRate: form.taxRate,
        currencyCode: form.currencyCode, bankName: form.bankName, bankAccount: form.bankAccount,
        accountantName: form.accountantName, accountantEmail: form.accountantEmail,
        defaultLocale: form.defaultLocale,
      });
      onSaved();
    } catch (err) {
      const fields = fieldErrors(err);
      setErrors(fields);
      if (Object.keys(fields).length === 0) setFormError(toMessage(err));
      else onError(err);
    } finally {
      setSaving(false);
    }
  }

  async function onLogoChange(file: File | undefined) {
    if (!file) return;
    setUploading(true);
    try {
      const updated = await api.uploadLogo(file);
      setLogoPath(updated.logoPath);
      toast.success(t('settings.saved'));
    } catch (err) {
      onError(err);
    } finally {
      setUploading(false);
      // Reset so selecting the same file again still fires a change event.
      if (fileInput.current) fileInput.current.value = '';
    }
  }

  async function removeLogo() {
    setUploading(true);
    try {
      const updated = await api.deleteLogo();
      setLogoPath(updated.logoPath);
    } catch (err) {
      onError(err);
    } finally {
      setUploading(false);
    }
  }

  return (
    <form onSubmit={onSubmit} noValidate className="stack">
      {formError && <ErrorAlert message={formError} />}

      <div className="card">
        <div className="card__head"><h2 className="card__title">{t('settings.company')}</h2></div>
        <div className="card__body">
          <div className="form-grid form-grid--2">
            <TextField label={t('settings.field.companyName')} required wrapperClassName="span-all"
              value={form.companyName} error={errors.companyName}
              onChange={(e) => set('companyName', e.target.value)} />
            <TextField label={t('settings.field.taxId')}
              value={form.taxId} error={errors.taxId} onChange={(e) => set('taxId', e.target.value)} />
            <TextField label={t('settings.field.phone')} type="tel"
              value={form.phone} error={errors.phone} onChange={(e) => set('phone', e.target.value)} />
            <TextField label={t('settings.field.email')} type="email" wrapperClassName="span-all"
              value={form.email} error={errors.email} onChange={(e) => set('email', e.target.value)} />
            <TextField label={t('settings.field.address')} wrapperClassName="span-all"
              value={form.address} error={errors.address} onChange={(e) => set('address', e.target.value)} />
            <TextField label={t('settings.field.city')}
              value={form.city} error={errors.city} onChange={(e) => set('city', e.target.value)} />
            <TextField label={t('settings.field.postalCode')}
              value={form.postalCode} error={errors.postalCode}
              onChange={(e) => set('postalCode', e.target.value)} />
            <TextField label={t('settings.field.region')} optional wrapperClassName="span-all"
              value={form.region} error={errors.region} onChange={(e) => set('region', e.target.value)} />
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card__head"><h2 className="card__title">{t('settings.invoicing')}</h2></div>
        <div className="card__body">
          <div className="form-grid form-grid--2">
            <TextField label={t('settings.field.taxRate')} required numeric inputMode="decimal"
              hint={t('settings.taxRateHint')}
              value={String(form.taxRate)} error={errors.taxRate}
              onChange={(e) => set('taxRate', Number(e.target.value.replace(',', '.')) || 0)} />
            <SelectField label={t('settings.field.currency')} value={form.currencyCode}
              error={errors.currencyCode}
              onChange={(e) => set('currencyCode', e.target.value)}>
              {currencies.map((currency) => (
                <option key={currency.code} value={currency.code}>
                  {currency.symbol} · {currency.name} ({currency.code})
                </option>
              ))}
            </SelectField>
            <SelectField label={t('settings.field.defaultLocale')} value={form.defaultLocale}
              onChange={(e) => set('defaultLocale', e.target.value as Locale)}>
              {LOCALES.map((code) => (
                <option key={code} value={code}>{LOCALE_LABELS[code]}</option>
              ))}
            </SelectField>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card__head"><h2 className="card__title">{t('settings.payment')}</h2></div>
        <div className="card__body">
          <div className="form-grid form-grid--2">
            <TextField label={t('settings.field.bankName')} optional
              value={form.bankName} error={errors.bankName}
              onChange={(e) => set('bankName', e.target.value)} />
            <TextField label={t('settings.field.bankAccount')} optional
              hint={t('settings.bankHint')}
              value={form.bankAccount} error={errors.bankAccount}
              onChange={(e) => set('bankAccount', e.target.value)} />
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card__head"><h2 className="card__title">{t('settings.accounting')}</h2></div>
        <div className="card__body">
          <div className="form-grid form-grid--2">
            <TextField label={t('settings.field.accountantName')} optional
              value={form.accountantName} error={errors.accountantName}
              onChange={(e) => set('accountantName', e.target.value)} />
            <TextField label={t('settings.field.accountantEmail')} optional type="email"
              hint={t('settings.accountingHint')}
              value={form.accountantEmail} error={errors.accountantEmail}
              onChange={(e) => set('accountantEmail', e.target.value)} />
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card__head"><h2 className="card__title">{t('settings.logo')}</h2></div>
        <div className="card__body">
          <div className="form-grid form-grid--2">
            <div className="logo-preview">
              {logoPath
                ? <img src={`/uploads/${logoPath}`} alt={t('settings.logo')} />
                : <div className="logo-preview__empty"><Icon name="image" size={24} /></div>}
            </div>
            <div className="stack stack--sm" style={{ justifyContent: 'center' }}>
              <p className="field__hint">{t('settings.logoHint')}</p>
              <input
                ref={fileInput}
                type="file"
                accept="image/png,image/jpeg"
                className="sr-only"
                id="logo-input"
                onChange={(e) => void onLogoChange(e.target.files?.[0])}
              />
              <div className="row">
                <label className="btn" htmlFor="logo-input">
                  {uploading ? <span className="spinner" /> : <Icon name="image" />}
                  {t('settings.logoUpload')}
                </label>
                {logoPath && (
                  <button type="button" className="btn btn--ghost" onClick={() => void removeLogo()}
                    disabled={uploading}>
                    <Icon name="trash" />
                    {t('settings.logoRemove')}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card__head"><h2 className="card__title">{t('backup.title')}</h2></div>
        <div className="card__body row row--between">
          <p className="muted" style={{ flex: 1, minWidth: 220 }}>{t('backup.subtitle')}</p>
          <Link to="/backups" className="btn">
            <Icon name="save" />{t('backup.title')}
          </Link>
        </div>
      </div>

      <div className="card">
        <div className="card__head"><h2 className="card__title">{t('verifactu.title')}</h2></div>
        <div className="card__body row row--between">
          <p className="muted" style={{ flex: 1, minWidth: 220 }}>{t('verifactu.subtitle')}</p>
          <Link to="/verifactu" className="btn">
            <Icon name="shield" />{t('verifactu.title')}
          </Link>
        </div>
      </div>

      <div className="row row--end">
        <button type="submit" className="btn btn--primary" disabled={saving}>
          {saving ? <span className="spinner" /> : <Icon name="save" />}
          {saving ? t('common.saving') : t('common.saveChanges')}
        </button>
      </div>
    </form>
  );
}

// ── mail server ─────────────────────────────────────────────────────────────

const SECURITY_LABELS: Record<MailSecurity, TranslationKey> = {
  none: 'mail.securityNone',
  starttls: 'mail.securityStarttls',
  tls: 'mail.securityTls',
};

function MailServerForm({ initial, onSaved }: {
  initial: MailSettings;
  onSaved: () => void;
}) {
  const t = useT();
  const toast = useToast();
  const toMessage = useErrorMessage();
  const { user } = useSession();

  const [form, setForm] = useState(initial);
  // Write-only: blank means "leave the stored password alone".
  const [password, setPassword] = useState('');
  const [testTo, setTestTo] = useState(user?.email ?? '');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string>();
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  // The relay's own words about a refusal — kept on screen rather than in a
  // toast, since it is what the next correction is made from.
  const [testDetail, setTestDetail] = useState<string>();

  useEffect(() => { setForm(initial); }, [initial]);
  useEffect(() => { setTestTo((current) => current || user?.email || ''); }, [user]);

  const set = <K extends keyof MailSettings>(key: K, value: MailSettings[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const payload = (): MailSettingsInput => ({
    enabled: form.enabled,
    host: form.host,
    port: form.port,
    security: form.security,
    username: form.username,
    tlsServername: form.tlsServername,
    fromName: form.fromName,
    fromEmail: form.fromEmail,
    bcc: form.bcc,
    // Only sent when typed, so saving the form never wipes a stored password.
    ...(password ? { password } : {}),
  });

  async function save(event?: FormEvent): Promise<boolean> {
    event?.preventDefault();
    setSaving(true);
    setErrors({});
    setFormError(undefined);
    try {
      await api.updateMailSettings(payload());
      setPassword('');
      onSaved();
      return true;
    } catch (err) {
      const fields = fieldErrors(err);
      setErrors(fields);
      if (Object.keys(fields).length === 0) setFormError(toMessage(err));
      return false;
    } finally {
      setSaving(false);
    }
  }

  async function test() {
    setTesting(true);
    setTestDetail(undefined);
    try {
      // Saved first: the server tests what is stored, not what is on screen.
      if (!await save()) return;
      const result = await api.testMailServer(testTo);
      if (result.ok) {
        toast.success(t('mail.testOk', { to: result.to ?? testTo }));
      } else {
        setTestDetail(result.detail ?? '');
        toast.error(t('mail.testFailed', { detail: result.detail ?? '' }));
      }
    } catch (err) {
      toast.error(toMessage(err));
    } finally {
      setTesting(false);
    }
  }

  return (
    <form onSubmit={save} noValidate className="stack" style={{ marginTop: 16 }}>
      {formError && <ErrorAlert message={formError} />}

      <div className="card">
        <div className="card__head">
          <h2 className="card__title">{t('mail.title')}</h2>
          <span className="muted">{t('mail.subtitle')}</span>
        </div>
        <div className="card__body stack">
          {form.fromEnvironment && (
            <div className="alert alert--info">
              <Icon name="alert-circle" />
              <div>{t('mail.fromEnvironment')}</div>
            </div>
          )}

          <Checkbox label={t('mail.enabled')} checked={form.enabled}
            onChange={(v) => set('enabled', v)} />
          {!form.enabled && <p className="field__hint">{t('mail.disabledNote')}</p>}

          <div className="form-grid form-grid--2">
            <TextField label={t('mail.host')} hint={t('mail.hostHint')} wrapperClassName="span-all"
              placeholder="smtp.example.com" autoComplete="off"
              value={form.host} error={errors.host}
              onChange={(e) => set('host', e.target.value)} />
            <TextField label={t('mail.port')} numeric inputMode="numeric"
              value={String(form.port)} error={errors.port}
              onChange={(e) => set('port', Number(e.target.value) || 0)} />
            <SelectField label={t('mail.security')} value={form.security} error={errors.security}
              onChange={(e) => set('security', e.target.value as MailSecurity)}>
              {(Object.keys(SECURITY_LABELS) as MailSecurity[]).map((value) => (
                <option key={value} value={value}>{t(SECURITY_LABELS[value])}</option>
              ))}
            </SelectField>

            <TextField label={t('mail.username')} optional hint={t('mail.usernameHint')}
              autoComplete="off" value={form.username} error={errors.username}
              onChange={(e) => set('username', e.target.value)} />
            <TextField label={t('mail.password')} type="password" optional autoComplete="new-password"
              hint={form.hasPassword ? t('mail.passwordStored') : undefined}
              value={password} onChange={(e) => setPassword(e.target.value)} />

            <TextField label={t('mail.tlsServername')} optional wrapperClassName="span-all"
              hint={t('mail.tlsServernameHint')} autoComplete="off"
              value={form.tlsServername} error={errors.tlsServername}
              onChange={(e) => set('tlsServername', e.target.value)} />
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card__head"><h2 className="card__title">{t('mail.sender')}</h2></div>
        <div className="card__body">
          <div className="form-grid form-grid--2">
            <TextField label={t('mail.fromName')} value={form.fromName} error={errors.fromName}
              onChange={(e) => set('fromName', e.target.value)} />
            <TextField label={t('mail.fromEmail')} type="email" autoComplete="off"
              placeholder="no-reply@example.com"
              value={form.fromEmail} error={errors.fromEmail}
              onChange={(e) => set('fromEmail', e.target.value)} />
            <p className="field__hint span-all">{t('mail.fromHint')}</p>
            <TextField label={t('mail.bcc')} optional wrapperClassName="span-all"
              hint={t('mail.bccHint')} value={form.bcc} error={errors.bcc}
              onChange={(e) => set('bcc', e.target.value)} />
          </div>
        </div>
        <div className="card__foot">
          {/* The field carries no hint of its own: a two-line field beside a
              button sits badly in a row that centres its items. */}
          <div className="row">
            <TextField label={t('mail.testTo')} type="email" autoComplete="off"
              placeholder={user?.email} value={testTo}
              onChange={(e) => setTestTo(e.target.value)} />
            <button type="button" className="btn" onClick={() => void test()}
              disabled={testing || saving || !form.enabled}>
              {testing ? <span className="spinner" /> : <Icon name="check-circle" />}
              {testing ? t('mail.testing') : t('mail.test')}
            </button>
            <span className="field__hint">{t('mail.saveFirst')}</span>
          </div>
          <button type="submit" className="btn btn--primary" disabled={saving}>
            {saving ? <span className="spinner" /> : <Icon name="save" />}
            {saving ? t('common.saving') : t('common.saveChanges')}
          </button>
        </div>
      </div>

      {testDetail !== undefined && (
        <ErrorAlert message={t('mail.testFailed', { detail: testDetail })} />
      )}
    </form>
  );
}
